import * as THREE from 'three';
import { HUMAN_NECK_BASE_RISE, type HumanProportions } from '../../../content/characters/rigs/human.js';
import { track } from './character-motion.js';

/** The trunk skin's cross-section at a height in the rest frame, before the sculpted bust mound. */
export function trunkSection(p: HumanProportions, y: number) {
  const h = p.hipsY, s = p.shoulderY, neckBase = s + HUMAN_NECK_BASE_RISE;
  const rows = [h - .1, h - .03, h + .06, h + .16, h + .27, s - .19, s - .115, s - .05, s - .01, s + .02, s + .049, s + .059, neckBase];
  const forward = [0, 0, 0, 0, 0, p.bust, p.bust * 1.6, p.bust * .4];
  const widths = [p.pelvisHalfWidth * .8, p.pelvisHalfWidth * .95, p.pelvisHalfWidth * .9, p.waistHalfWidth, p.ribHalfWidth, p.chestHalfWidth * .98, p.chestHalfWidth, p.shoulderHalfWidth * .9, p.shoulderHalfWidth, p.shoulderHalfWidth * .9, p.shoulderHalfWidth * .6, p.neckRadius, p.neckRadius];
  let i = 1; while (i < rows.length - 1 && y > rows[i]) i++;
  const t = THREE.MathUtils.clamp((y - rows[i - 1]) / (rows[i] - rows[i - 1]), 0, 1), mix = (a: number, b: number) => a + (b - a) * t;
  const front = mix(p.trunk.front[i - 1] + (forward[i - 1] ?? 0), p.trunk.front[i] + (forward[i] ?? 0)), back = mix(p.trunk.back[i - 1], p.trunk.back[i]);
  const centre = (front - back) / 2 + p.neckZ * THREE.MathUtils.smoothstep(y, s - .05, neckBase);
  return { width: mix(widths[i - 1], widths[i]), front, back, centre };
}

/** The front skin surface, including the bust, at a height and sideways offset in the rest frame. */
export function trunkFront(p: HumanProportions, x: number, y: number) {
  const { width, front, back, centre } = trunkSection(p, y), s = p.shoulderY;
  const across = Math.min(.98, Math.abs(x) / width);
  // The sculpted bust is a mound either side of the breastbone, not on it.
  const mound = p.bust ? p.bust * 2.1 * Math.exp(-(((y - (s - .13)) / .07) ** 2)) * Math.exp(-(((Math.abs(x) - p.chestHalfWidth * p.bustShape.centre) / .06) ** 2)) : 0;
  return centre + (front + back) / 2 * (1 - across ** 2.3) ** (1 / 2.3) + mound;
}

/** The free hand's path over the front of the body, in a loop that goes up over the body one way and returns the other. */
export function caressPath(p: HumanProportions, phase: number, clearance = .032) {
  // Up across the belly and breastbone to the collarbone and back down the flank: an ellipse leaning from the right hip toward the left shoulder.
  const around = phase - .6, u = .5 + .5 * Math.sin(around), sweep = Math.cos(around);
  const low = p.hipsY + .05, high = p.shoulderY - .03;
  const y = low + (high - low) * track(u, [[0, 0], [.5, .5], [1, 1]]);
  // The hand rises along the midline and slides back over the flank on the way down: the two legs of the loop are a hand's width apart.
  const x = THREE.MathUtils.lerp(.03, -.1, u) + sweep * .045;
  return new THREE.Vector3(x, y, trunkFront(p, x, y) + clearance);
}

/**
 * Where a hand rests when a standing figure holds its hands together (a bearing's `hold`), in the rest frame: clasped over the lower belly, or
 * behind the back at the small of it with the right hand a little lower and further back, holding the left wrist.
 */
export function heldHand(p: HumanProportions, hold: 'front' | 'behind', side: number, clearance = .045) {
  if (hold === 'behind') {
    const y = p.hipsY + .1 - (side > 0 ? .025 : 0), { front, back, centre } = trunkSection(p, y);
    return new THREE.Vector3(side * .035, y, centre - (front + back) / 2 - clearance - (side > 0 ? .02 : 0));
  }
  const y = p.hipsY + .07 + (side > 0 ? .015 : 0), x = side * .03;
  return new THREE.Vector3(x, y, trunkFront(p, x, y) + clearance);
}
